import CloseIcon from '@mui/icons-material/Close';
import { FormControl, Icon, IconButton } from '@mui/material';
import Alert from '@mui/material/Alert';
import arrayToTree from 'array-to-tree';
import { Button } from 'global/components/Button';
import DndTable from 'global/components/DragTable';
import { DropDown } from 'global/components/dropdown';
import Form from 'global/components/form';
import Modal from 'global/components/modal';
import { Row } from 'global/components/row';
import SearchBar from 'global/components/SearchBar';
import SnackAlerts from 'global/components/snack-alerts';
import useDebounce from 'global/hooks/useDebounce';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useUser } from 'global/hooks/useUser';
import { get, set, size } from 'global/lib/lodash';
import { Loader, useScreen } from 'global/styles';
import { uppercaseFirst } from 'global/utils';
import * as moment from 'moment';
import { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import sendData from 'redux/actions/sendData';
import { realmsToString } from '../../../global/utils';
import { UploadFiles } from '../../../redux/actions/imageUploader';
import { RealmAssignmentsModal } from './realm-assignments';
import {
  ADMIN_SEARCH_BAR,
  AdminContainer,
  AdminContainerCentered,
  AdminFlexRow,
  AdminContentRoot,
  FlexRowPad20,
  StyledFormControl,
  StyledTable,
} from './styles';

export const StatusTypes = [
  { title: 'Archived', value: 'archived' },
  { title: 'Active', value: 'active' },
  { title: 'Draft', value: 'draft' },
  { title: 'Shared', value: 'shared' },
];
export const DocumentTypes = [
  { title: 'Document', value: 'document' },
  { title: 'Course', value: 'course' },
];

export default withRouter((props) => {
  const { isMobile } = useScreen();
  const [error, setError] = useState();
  const [alerts, setAlerts] = useState();
  const [state, setState] = useState({
    status: StatusTypes,
    statusValue: 0,
    typeValue: 0,
    types: DocumentTypes,
    selected: {},
    loading: false,
    search: '',
  });
  const [createProps, setCreateProps] = useState({
    open: false,
    title: 'CREATE NEW EDITION',
    onClose: () => setCreateProps((s) => ({ ...s, open: false })),
  });

  const { me } = useUser();

  const debSearch = useDebounce(state.search, 700);

  const [params, setParams] = useState({
    page: { size: 500, number: 1 },
    include: 'realm,userGroups,realms',
  });

  const transformData = (d) => {
    const myRealmId = Number(get(me, 'realm.id'));

    d = d.map((item) => {
      const latestId = item.latestId === Number(item.id) ? null : item.latestId;
      const isOwnerRealm = item.ownerRealmId === myRealmId;

      // If the edition is owned by a different realm, keep it but mark it as shared
      // so it appears in the list with the correct status.
      return {
        ...item,
        latestId,
        isShared: !isOwnerRealm,
        realms: isOwnerRealm ? item.realms : [{ tier: 'shared', name: 'Shared' }],
      };
    });
    let treed = arrayToTree(d, { parentProperty: 'parentId' });

    treed = treed.map((tr) => {
      const all = [];

      const loopChildren = (arr, parent) => {
        arr.map((t) => {
          all.push({ ...t, parent });
          if (t.children) {
            loopChildren(t.children, t);
          }
        });
      };

      loopChildren(tr.children || []);

      tr.children = all;
      return tr;
    });

    let total = [];

    treed.map((tr) => {
      const trall = [tr, ...(tr.children || [])].map((t) => ({ ...t, parent: tr })).reverse();
      const top = trall.find((t) => t.id == tr.latestId);

      if (top) {
        delete top.parent;
        total = [...total, top, ...trall.filter((t) => t.id != top.id)];
      } else {
        const trallNoTop = [tr, ...(tr.children || [])].map((t) => ({ ...t, parent: tr }));
        const sortedByCreated = trallNoTop.slice().sort((a, b) => moment(get(b, 'createdAt')).valueOf() - moment(get(a, 'createdAt')).valueOf());
        const newest = sortedByCreated[0];
        if (newest) {
          delete newest.parent;
          total = [...total, newest, ...sortedByCreated.filter((t) => t.id != newest.id)];
        } else {
          total = [...total, ...sortedByCreated];
        }
      }
    });

    // Show shared editions at the bottom of the list
    const shared = [];
    const unshared = [];
    total.forEach((row) => {
      (row.isShared ? shared : unshared).push(row);
    });

    return [...unshared, ...shared];
  };

  const editions = useRetrieveData(
    { url: '/editions' },
    {
      build: ['editions'],
      useEffect: true,
      params,
      transformData,
    }
  );

  const userGroups = useRetrieveData({ url: '/user-groups' }, { build: ['userGroups'] });

  const { data } = editions;

  useEffect(() => {
    setParams((prev) => {
      const next = {
        ...prev,
        page: { size: 500, number: 1 },
        include: 'realm,userGroups,realms',
        filter: { ...(prev.filter || {}) },
      };

      // edition_type filter
      if (state.typeValue) {
        set(next, 'filter.edition_type', state.typeValue);
      } else if (state.typeValue === 0) {
        set(next, 'filter.edition_type', state.types.map((t) => t.value).join(','));
      }

      // status filter
      if (state.statusValue) {
        set(next, 'filter.status', state.statusValue);
      } else if (state.statusValue === 0) {
        set(next, 'filter.status', state.status.map((t) => t.value).join(','));
      }

      // name search filter
      if (debSearch) {
        set(next, 'filter.name', debSearch);
      } else {
        if (get(next, 'filter.name')) {
          delete next.filter.name;
        }
      }

      // Clean up empty filter object
      if (next.filter && Object.keys(next.filter).length === 0) {
        delete next.filter;
      }

      // Prevent triggering a fetch if nothing meaningful changed.
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [state.typeValue, state.statusValue, debSearch]);

  const [locations, setLocations] = useState({
    modalProps: {
      open: false,
      onClose: () => {
        setLocations((s) => ({ ...s, modalProps: { ...s.modalProps, open: false }, onSubmit: undefined }));
        // update();
      },
    },
  });

  const handleSubmit = async (docs, edition) => {
    const config = { docs: {} };
    const clientVendorId = get(edition, 'data.clientVendorId', null);
    Object.keys(docs).map((k) => {
      const doc = docs[k];
      doc.items.map((item) => {
        if (size(item.rooms)) {
          item.rooms.map((r) => {
            const key = `docs.${item.id}`;
            set(config, `${key}.realms`, [...get(config, `${key}.realms`, []), { id: r.id, name: r.name }]);
            set(config, `${key}.documentableId`, item.documentableId);
            set(config, `${key}.documentableType`, item.documentableType);
          });
        }
      });
    });

    if (!clientVendorId) {
      setAlerts({ message: 'No client vendor associated with this edition', severity: 'error' });
      return;
    }

    const data = {
      data: {
        id: clientVendorId.toString(),
        type: 'client-vendors',
        editions: {
          id: edition?.data?.id,
          type: 'editions',
          config,
        },
      },
    };

    await sendData({
      url: `/client-vendors/${clientVendorId.toString()}`,
      method: 'patch',
      data,
    });

    locations.modalProps.onClose();
  };

  const selectRow = (row) => {
    if (row.isShared) {
      setLocations((s) => ({
        ...s,
        editionId: row.id,
        selectedEdition: row,
        modalProps: {
          ...s.modalProps,
          open: true,
          title: `Edit Location Assignments for ${get(row, 'name')}`,
        },
        onSubmit: handleSubmit,
      }));
    } else {
      props.history.push(`/account/content/edition/${row.id}`, { from: 'account/content' });
    }
  };

  const openCreate = () => {
    let formState = {};

    const close = () => {
      setCreateProps((s) => ({
        ...s,
        open: false,
      }));
    };

    const submit = async () => {
      const hasImage = formState.type === 'course' ? size(formState.images) : true;
      if (!formState.type || !formState.name || !hasImage) {
        return alert('Please complete all fields');
      }
      try {
        const realms = get(formState, 'realms')
          ? Object.keys(formState.realms)
              .filter((k) => get(formState, `realms.${k}.id`))
              .map((k) => ({ type: 'realms', id: k }))
          : undefined;
        const userGroups = get(formState, 'userGroups') ? formState.userGroups.map((id) => ({ type: 'user-groups', id })) : undefined;

        const data = {
          data: {
            type: 'editions',
            attributes: {
              images: formState.images,
              name: formState.name,
              realm_id: get(me, 'realm.id'),
              edition_type: formState.type,
              status: 'draft',
            },
            relationships: {
              realms: realms ? { data: realms } : undefined,
              userGroups: userGroups ? { data: userGroups } : undefined,
            },
          },
        };

        await UploadFiles(data);

        await sendData({
          url: `/editions`,
          method: 'post',
          data,
        });

        editions.update();
      } catch (err) {
        setError(err.message);
      } finally {
        close();
      }
    };

    setCreateProps((s) => ({
      ...s,
      open: true,
      content: <CreateEditionForm onFormState={(fs) => (formState = fs)} userGroups={userGroups} />,
      buttons: [{ title: 'Create', onClick: submit, startIcon: <Icon>add</Icon> }],
    }));
  };

  return state.loading ? (
    <AdminContainerCentered isMobile={isMobile}>
      <Loader />
    </AdminContainerCentered>
  ) : (
    <AdminContentRoot isMobile={isMobile}>
    <AdminContainer isMobile={isMobile}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <div>
        <Button onClick={openCreate} startIcon={<Icon>add</Icon>}>
          CREATE EDITION
        </Button>
        <SearchBar changeSearch={(t) => setState((s) => ({ ...s, search: t }))} containerStyle={ADMIN_SEARCH_BAR} />
      </div>

      <AdminFlexRow>
        <StyledFormControl>
          <DropDown
            label="Status"
            value={state.statusValue}
            onChange={(e) => {
              setState((s) => ({ ...s, statusValue: e.target.value }));
            }}
            options={[{ id: 0, name: 'Any' }].concat(
              state.status.map((sc) => ({
                id: sc.value,
                name: sc.title,
              }))
            )}
          />
        </StyledFormControl>

        <StyledFormControl>
          <DropDown
            label="Type"
            value={state.typeValue}
            onChange={(e) => {
              setState((s) => ({ ...s, typeValue: e.target.value }));
            }}
            options={[{ id: 0, name: 'Any' }].concat(
              state.types.map((sc) => ({
                id: sc.value,
                name: sc.title,
              }))
            )}
          />
        </StyledFormControl>
      </AdminFlexRow>

      <StyledTable>
        {editions.loading && (
          <FlexRowPad20 isMobile={isMobile}>
            <Loader />
          </FlexRowPad20>
        )}
        {!editions.loading && <DndTable columns={COLUMNS} canDrag={false} onSelectRow={selectRow} items={data} />}
      </StyledTable>

      <RealmAssignmentsModal {...locations} />
      <Modal {...createProps} />
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />
    </AdminContainer>
    </AdminContentRoot>
  );
});

const COLUMNS = [
  {
    field: 'name',
    title: 'Title',
    width: 200,
    render: (row) => {
      return (
        <Row>
          {row.parent && <Icon className="parentIcon">{'keyboard_return'}</Icon>}
          {get(row, 'name')}
        </Row>
      );
    },
  },
  {
    field: 'editionType',
    title: 'Type',
    width: 200,
    render: (row, text) => {
      return uppercaseFirst(text);
    },
  },
  {
    field: 'realm',
    title: 'Access',
    width: 250,
    render: (row, text) => {
      return (
        <div>
          <Row style={{ flexWrap: 'wrap' }}>{row.isShared ? <em>{realmsToString(row.realms, { includeIcon: true })}</em> : realmsToString(row.realms, { includeIcon: true })}</Row>
          <div>{(get(row, 'userGroups', []) || []).map((u) => u.name).join(', ')}</div>
        </div>
      );
    },
  },
  {
    field: 'status',
    title: 'Status',
    width: 100,
    render: (row, text) => {
      return uppercaseFirst(text);
    },
  },
  {
    field: 'createdAt',
    title: 'Created',
    width: 100,
    render: (row, text) => {
      return moment(text).format('ll');
    },
  },
];

const CreateEditionForm = (props) => {
  const [fs, setfs] = useState({});
  const { me, userBuilderRealm } = useUser();

  useEffect(() => {
    props.onFormState && props.onFormState(fs);
  }, [fs]);

  return (
    <Form
      showAllScreens
      onFormState={setfs}
      screens={[
        {
          inputs: [
            {
              id: 'type',
              type: 'ToggleButton',
              options: DocumentTypes,
              props: {
                exclusive: true,
                label: 'Type',
              },
            },
            {
              id: 'name',
              type: 'TextField',
              defaultValue: () => '',
              props: {
                label: 'Title',
                placeholder: 'Title',
                autoFocus: true,
                helperText: `A new Edition will be created in a "Draft" state, with no content. To add content after you create it, open the newly created Edition from the content list.`,
              },
            },
            fs.type === 'course' && {
              id: 'images',
              type: 'DropArea',
              required: true,
              props: {
                label: 'Cover Image',
                multiple: false,
                showCaption: false,
              },
            },
            {
              id: 'realms',
              type: 'DropRealms',
              defaultValue: () => ({ [userBuilderRealm.id]: userBuilderRealm }),
              props: {
                label: 'Access',
                onlyTiers: ['builder', 'division'],
                hideNonUserBuilders: true,
              },
            },
            {
              id: 'userGroups',
              type: 'Select',
              data: get(props, 'userGroups.data', []),
              item: (item) => ({ value: item.id, title: item.name }),
              defaultValue: () => get(me, 'userGroups', []).map((u) => u.id),
              props: {
                multiple: true,
                label: 'User Groups',
                helperText: `A new Edition will be created in a "Draft" state, with no content. To add content after you create it, open the newly created Edition from the content list.`,
              },
            },
          ].filter((f) => !!f),
        },
      ]}
    />
  );
};
